import type { SellingUnitOfMeasure } from '@snap-types/models/enums';
import { FIELD_IDS } from '@ui/features/configurator/data/fixtures/cold-headed-fastener';
import type {
  ConfiguratorFieldState,
  ConfiguratorFieldType,
} from '@ui/features/configurator/types';

import { maybeGetFieldByStepId } from './field-helpers';

type ConfiguratorFieldLabel = {
  name: string;
  description?: string;
};

type FieldLabelState = {
  fields: ConfiguratorFieldState;
  sellingUnitOfMeasure: SellingUnitOfMeasure;
};

/**
 * Resolves the label and tooltip for a field whose unit cannot be stated when
 * the configuration is written.
 *
 * Three fields qualify. Quantity and Washer Material Cost follow the unit the
 * site sells in, and Material Cost follows the stocking unit of whichever
 * Material is selected. Every other field keeps the label its configuration
 * gave it — including the two weight fields, which are per piece on every site
 * and already say so.
 *
 * `state` is read inside the branches rather than destructured so that a field
 * needing no unit subscribes to no store state.
 */
export function getFieldLabel(
  field: ConfiguratorFieldType,
  state: FieldLabelState,
): ConfiguratorFieldLabel {
  switch (field.fieldId) {
    /**
     * The configurator works in the unit the site sells in, so on an `M` site
     * the number a user types for quantity is thousands of pieces. Washer
     * material cost reaches the cost engine against a `qtyPer` of 1, which
     * makes it money per selling unit rather than money per pound of material
     * the way `Material Cost` is.
     */
    case FIELD_IDS.quantity:
    case FIELD_IDS.washer_material_cost:
      return {
        name: `${field.name} (${state.sellingUnitOfMeasure})`,
        description: field.description,
      };

    case FIELD_IDS.material_cost: {
      const stockUm = getMaterialStockUm(field, state.fields);

      return {
        name: stockUm ? `${field.name} (per ${stockUm})` : field.name,
        description: field.description,
      };
    }

    default:
      return { name: field.name, description: field.description };
  }
}

/**
 * The stocking unit of the Material selected alongside a cost field. Both
 * fields live in the same step, and there is no unit to name until a Material
 * has been chosen.
 */
function getMaterialStockUm(
  field: ConfiguratorFieldType,
  fields: ConfiguratorFieldState,
) {
  const materialField = maybeGetFieldByStepId(
    field.stepId,
    FIELD_IDS.material,
    fields,
  );

  if (materialField?.inputType !== 'material') {
    return null;
  }

  return materialField.value?.meta?.stockUm ?? null;
}
